import { DepartmentNames } from "../utils/models";
import { DEFAULT_COLOR_BY_ROLE, isSameHexColor, UKG_DARK_TEAL } from "./palette";
import { DEFAULT_LOGO, type LogoId } from "./tokens";

/** The logo and primary color a department opens with. */
export interface DepartmentLook {
	logo: LogoId;
	primary: string;
}

// Departments that open with their own look instead of the InHouse one.
// Ids must match exactly (no prefix or case matching).
const DEPARTMENT_LOOKS: Readonly<Partial<Record<string, DepartmentLook>>> = {
	[DepartmentNames.BayviewGeneralHospital]: { logo: "ukg-plain", primary: UKG_DARK_TEAL },
};

export const getDepartmentLook = (departmentId: string | null): DepartmentLook | undefined => {
	if (!departmentId) return undefined;
	return DEPARTMENT_LOOKS[departmentId];
};

/**
 * What to do with the saved look when the chosen department changes:
 * - apply: save the department's look (logo + primary) and mark it as applied.
 * - revert: go back to the InHouse logo and primary, and clear the mark.
 * - release: only clear the mark; the user changed the look, so keep it.
 * - none: leave everything as it is.
 */
export type DepartmentLookAction = { type: "apply"; look: DepartmentLook } | { type: "revert" } | { type: "release" } | { type: "none" };

export interface DepartmentLookState {
	/** Chosen department id. "" while the department is still loading. */
	departmentId: string;
	logo: LogoId;
	primary: string;
	/** The department whose look was last applied on this device, if any. */
	appliedFor: string | null;
}

const matchesLook = (look: DepartmentLook, logo: LogoId, primary: string): boolean => logo === look.logo && isSameHexColor(primary, look.primary);

const DEFAULT_LOOK: DepartmentLook = { logo: DEFAULT_LOGO, primary: DEFAULT_COLOR_BY_ROLE.primary };

export const resolveDepartmentLookAction = ({ departmentId, logo, primary, appliedFor }: DepartmentLookState): DepartmentLookAction => {
	if (!departmentId) return { type: "none" };

	const look = getDepartmentLook(departmentId);
	const appliedLook = getDepartmentLook(appliedFor);
	// True when the look applied earlier is still in place, not changed by the user.
	const appliedLookUntouched = appliedLook !== undefined && matchesLook(appliedLook, logo, primary);

	if (look) {
		if (appliedFor === departmentId) return { type: "none" };
		// Only seed over the InHouse look, or over another department's untouched look.
		// Never replace a look the user picked.
		return matchesLook(DEFAULT_LOOK, logo, primary) || appliedLookUntouched ? { type: "apply", look } : { type: "none" };
	}

	if (!appliedFor) return { type: "none" };
	return appliedLookUntouched ? { type: "revert" } : { type: "release" };
};
